import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from '@hugeicons/react'
import { useTranslation } from 'react-i18next'
import UserAvatar from '../UserAvatar'
import Username from '../Username'
import { ArrowReloadHorizontalIcon } from '@hugeicons/core-free-icons'
import { useMemo } from 'react'
import { Event } from '@nostr/tools/wasm'
import NoteOptions from '../NoteOptions'
import { FormattedTimestamp } from '../FormattedTimestamp'

/**
 * - reposters.length === 1: show "Alice reposted"
 * - reposters.length === 2: show "Alice, Bob reposted"
 * - reposters.length === 3: show "Alice, Bob, Charlie reposted"
 * - reposters.length > 3: show "Alice, Bob, and x others reposted" (with hover card showing avatars of others)
 *
 * When event is provided, the "reposted" label opens the context menu for the repost event.
 */
export default function RepostDescription({
  reposters,
  className,
  event
}: {
  reposters?: string[]
  className?: string
  event?: Event
}) {
  const { t } = useTranslation()
  const remainingReposters = useMemo(() => reposters?.slice(2) || [], [reposters])

  if (!reposters?.length) return null

  return (
    <div className={cn('flex gap-1 text-sm items-center text-muted-foreground mb-1', className)}>
      <HugeiconsIcon icon={ArrowReloadHorizontalIcon} size={16} className="shrink-0" />
      <Username
        key={reposters[0]}
        user={reposters[0]}
        className={cn('font-semibold truncate', reposters.length > 1 && 'after:content-[","]')}
        skeletonClassName="h-3"
      />
      {reposters.length > 1 && (
        <Username
          key={reposters[1]}
          user={reposters[1]}
          className={cn('font-semibold truncate', reposters.length === 3 && 'after:content-[","]')}
          skeletonClassName="h-3"
        />
      )}
      {reposters.length > 3 ? (
        <AndXOthers reposters={remainingReposters} />
      ) : reposters.length === 3 ? (
        <Username
          key={reposters[2]}
          user={reposters[2]}
          className={cn('font-semibold truncate')}
          skeletonClassName="h-3"
        />
      ) : null}
      {event ? (
        <>
          <NoteOptions
            event={event}
            customTrigger={
              <span className="hover:underline cursor-pointer shrink-0">{t('reposted')}</span>
            }
            className="shrink-0"
          />
          <FormattedTimestamp timestamp={event.created_at} />
        </>
      ) : (
        <div className="shrink-0">{t('reposted')}</div>
      )}
    </div>
  )
}

function AndXOthers({ reposters }: { reposters: string[] }) {
  const { t } = useTranslation()

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <span className="shrink-0 hover:underline">
          {t('and {{x}} others', { x: reposters.length })}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-fit max-w-60 flex flex-wrap p-2">
        {reposters.map((pubkey) => (
          <div key={pubkey} className="p-2">
            <UserAvatar key={pubkey} user={pubkey} size="small" />
          </div>
        ))}
      </HoverCardContent>
    </HoverCard>
  )
}
